import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { GarageEmptyState } from '@/components/GarageEmptyState';
import { Heading } from '@/components/Heading';
import { MutedNavLink } from '@/components/MutedNavLink';
import { Pill } from '@/components/Pill';
import { SkeletonList } from '@/components/Skeleton';
import { F } from '@/design/fonts';
import { SPACE, T } from '@/design/tokens';
import { useAuth } from '@/features/auth/useAuth';
import { useIsFirstRun } from '@/hooks/useIsFirstRun';
import { useUserVehicles } from '@/hooks/useUserVehicles';
import { env } from '@/lib/env';
import { Link } from 'react-router-dom';

export function VehicleList() {
  const { user } = useAuth();
  const { data: vehicles, loading, error, hasPendingWrites } = useUserVehicles(user?.uid);
  const isFirstRun = useIsFirstRun();

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Heading level={1} size="xl">
          Garage
        </Heading>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
          {env.VITE_INSIGHTS_ENABLED ? <MutedNavLink to="/insights">Insights</MutedNavLink> : null}
          {env.VITE_TRANSFER_ENABLED ? (
            <MutedNavLink to="/transfer/accept">Accept a transfer</MutedNavLink>
          ) : null}
          <Link to="/vehicles/new" style={{ textDecoration: 'none' }} aria-label="Add a vehicle">
            <Button size="sm" tabIndex={-1}>
              + Add vehicle
            </Button>
          </Link>
          {hasPendingWrites ? <Pill color="orange">Syncing</Pill> : null}
        </div>
      </div>

      {loading ? (
        <div style={{ marginTop: SPACE.lg }}>
          <SkeletonList count={2} />
        </div>
      ) : error ? (
        <Card accent="red" style={{ marginTop: SPACE.lg }}>
          <p style={{ margin: 0, color: T.red }}>Couldn't load garage: {error.message}</p>
        </Card>
      ) : isFirstRun ? (
        <GarageEmptyState />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: `${SPACE.lg}px 0 0`,
            display: 'grid',
            gap: SPACE.md,
          }}
        >
          {vehicles.map((v) => (
            <li key={v.id}>
              <Link
                to={`/vehicles/${v.id}`}
                aria-label={`${v.year} ${v.make} ${v.model}`}
                style={{ display: 'block' }}
              >
                <Card>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: SPACE.md,
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: F.display, fontSize: 20, color: T.text }}>
                        {v.nickname ?? `${v.year} ${v.make} ${v.model}`}
                      </div>
                      <div style={{ color: T.textMuted, fontSize: 13, marginTop: SPACE.xs }}>
                        {v.year} · {v.make} · {v.model}
                        {v.chassisCode ? ` · ${v.chassisCode}` : ''}
                      </div>
                    </div>
                    <span style={{ color: T.textMuted, fontSize: 18 }} aria-hidden="true">
                      ›
                    </span>
                  </div>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
